













































































































































































































































































































































































import type { ApolloQueryResult } from '@apollo/client/core';
import type { PropType, Ref } from '@nuxtjs/composition-api';
// eslint-disable-next-line no-duplicate-imports
import {
  computed,
  defineComponent,
  nextTick,
  onBeforeMount,
  onMounted,
  onUnmounted,
  ref,
  toRef,
  useContext,
  useRoute,
  watch
} from '@nuxtjs/composition-api';
import debounce from 'lodash/debounce';
import ProductMainGallery from 'organisms/product/ProductMainGallery/index.vue';
import LazyHydrate from 'vue-lazy-hydration';

import type { CmsBlock } from '~/composables';
// eslint-disable-next-line no-duplicate-imports
import { useConfig, useContent, useUiState } from '~/composables';
import { useNarVar, useScreenSize } from '~/diptyqueTheme/composable';
import type { EngravingConfig } from '~/diptyqueTheme/composable/useEngraving/types';
import {
  formatEventPrice,
  getEditorialData,
  itemBrand
} from '~/diptyqueTheme/composable/useGoogleTagManager/helpers';
import { useKlarna } from '~/diptyqueTheme/composable/useKlarna/useKlarna';
import soClozScript from '~/diptyqueTheme/customQueries/magento/soClozScript';
import { validateFraction } from '~/diptyqueTheme/helpers/priceFormatter';
import {
  getProductCapacity,
  getProductPDPCombinedDescription,
  getProductPreorderButtonLabel,
  getProductPreorderStockNotice,
  getProductReplenishable,
  getProductSubtitle,
  getProductVariantName,
  isProductPreoderEnabled,
  shouldShowEstimationDeliveryDate
} from '~/diptyqueTheme/helpers/productGetters';
import { useAddToCart } from '~/helpers/cart/addToCart';
import { Logger } from '~/helpers/logger';
import { scrollToElement } from '~/helpers/scrollToElement';
import { useFixedPanel } from '~/modules/catalog/product/composables/useFixedPanel';
import productGetters, {
  getBasePrice,
  getBreadcrumbs,
  getName as getProductFallbackName,
  getPrice as getProductPrice
} from '~/modules/catalog/product/getters/productGetters';
import { useProductStore } from '~/modules/catalog/product/stores/product';
import type {
  Product,
  ProductTitleData,
  SoCloZQueryReturnType
} from '~/modules/catalog/product/types';
import { useCartStore } from '~/modules/core/stores/cart';
import type { ProductImage, ProductInterface } from '~/modules/GraphQL/types';
import { ProductStockStatus } from '~/modules/GraphQL/types';

import ProductProductSummary from '../../atoms/ProductSummary.vue';

declare global {
  interface Window {
    Klarna: any;
  }
}
export default defineComponent({
  name: 'SimpleProduct',
  components: {
    LazyHydrate,
    ProductSummary: ProductProductSummary,
    ProductMainGallery,
    ButtonNotifyMe: () => import('atoms/ButtonNotifyMe.vue'),
    ButtonSoldOut: () => import('atoms/ButtonSoldOut.vue'),
    SimpleProductFixedPanel: () => import('./SimpleProductFixedPanel.vue'),
    VaimoTruncatedText: () => import('atoms/VaimoTruncatedText.vue'),
    VaimoButton: () => import('atoms/VaimoButton.vue'),
    EngravingPleat: () => import('molecules/EngravingPleat/EngravingPleat.vue'),
    VaimoCrossSellProducts: () =>
      import('organisms/VaimoCrossSellProducts/VaimoCrossSellProducts.vue'),
    VaimoSidebar: () => import('organisms/VaimoSidebar.vue'),
    VaimoInformationPleats: () =>
      import('organisms/VaimoInformationPleats.vue'),
    InformationPleat: () =>
      import(
        '@/diptyqueTheme/components/molecules/InformationPleat/InformationPleat.vue'
      ),
    ProductRelatedProducts: () =>
      import('../../organisms/ProductRelatedProducts.vue'),
    SectionsList: () => import('templates/SectionsList/SectionsList.vue'),
    ProductReinsurance: () =>
      import(
        '~/modules/catalog/product/components/organisms/ProductReinsurance.vue'
      ),
    EngravingLayer: () => import('organisms/product/EngravingLayer.vue')
  },
  transition: 'fade',
  props: {
    product: {
      type: Object as PropType<Product | null>,
      default: null
    },
    productBlocks: {
      required: false,
      type: Array,
      default: () => []
    },
    engravingConfig: {
      type: Object as PropType<EngravingConfig | null>,
      default: null
    }
  },
  setup(props) {
    const observer: Ref<MutationObserver | null> = ref(null);
    const {
      app: { $gtm, $vsf }
    } = useContext();
    const product = toRef(props, 'product');
    const { isDesktop } = useScreenSize();
    const { config } = useConfig();
    const { addItemToCart, loading } = useAddToCart();
    const productStore = useProductStore();
    const { getEstimatedDeliveryDate, estimatedDeliveryDate, formatDate } =
      useNarVar();
    const { loadBlocks } = useContent();
    const cartStore = useCartStore();
    const isMerging = computed(() => cartStore.is_data_loading);
    const {
      openNotifyMeModal,
      openEngravingInfo,
      hideEngravingInfo,
      isEngravingInfoOpen
    } = useUiState();

    // Toggle Notify Me modal
    const notifyMe = () => {
      if (!product.value?.id) return;
      openNotifyMeModal(product.value.id);
    };

    const soClozHtml = ref(null);
    const isInformationPleatsSidebarOpen = ref(false);
    const pleatData = ref({});
    const relatedProductsWrapp = ref<HTMLElement | null>(null);
    const relatedProductsWidth = ref(0);
    let resizeObserver = null;

    const RESIZE_DEBOUNCE_DELAY = 300;

    /**
     * Returns edito images from the product
     * Edito images **do not** have correct state for (set to null):
     * - position (sort order)
     * - disabled
     */
    const getEditoImages = () => {
      const product = props.product || {};
      return ['edito_image', 'edito_image_2', 'edito_image_3', 'night_vision']
        .map(
          (key) =>
            product[key] && {
              ...product[key],
              label: key === 'night_vision' ? 'night' : 'edito'
            }
        )
        .filter(Boolean);
    };

    const productImages = computed<ProductImage[]>(() => {
      const mediaGallery = props.product?.media_gallery || [];
      const editoImages = getEditoImages();
      const editoImagesMap = new Map(
        editoImages.map((item) => [item.url, item])
      );

      return mediaGallery
        .filter((item) => item.disabled !== true)
        .map((item) => {
          const match = editoImagesMap.get(item.url);

          return match === undefined
            ? item
            : {
                ...item,
                label: match.label
              };
        });
    });

    const handlePleatClick = (pleat) => {
      isInformationPleatsSidebarOpen.value = true;
      // eslint-disable-next-line no-useless-escape
      const regex = /{{media url=\&quot\;(\S+)\&quot\;}}/g;
      pleat.value = pleat.value.replace(
        regex,
        'https://www.diptyqueparis.com/media/$1'
      );
      pleatData.value = pleat;
    };

    const formatedDate = computed(() => {
      if (estimatedDeliveryDate.value) {
        return formatDate(estimatedDeliveryDate.value);
      }
    });

    const productPrice = computed(() => getProductPrice(props.product).regular);
    const productSpecialPrice = computed(
      () => getProductPrice(props.product).special
    );

    const {
      isKlarnaEnabled,
      isPriceEligibleForInstalments,
      loadKlarnaScript,
      refreshKlarnaMessaging
    } = useKlarna(config, productPrice);

    const convertToMinorUnits = computed(() => {
      const minorUnitMultiplier = 100;
      const parsedPrice =
        typeof productPrice.value === 'string'
          ? parseFloat(productPrice.value)
          : productPrice.value;

      return Math.round(parsedPrice * minorUnitMultiplier);
    });

    const shouldShowEstimatedDelivery = computed(
      () =>
        estimatedDeliveryDate.value &&
        !productData.value.isPreOrder &&
        productData.value.showEstimationDeliveryDate
    );

    const transformedLocale =
      config.value?.klarna_pay_instalments_locale?.replace('_', '-');

    const hideSidebar = () => {
      isInformationPleatsSidebarOpen.value = false;
      pleatData.value = {};
    };

    const isOutOfStock = computed<boolean>(() => {
      return (
        props.product?.stock_status === ProductStockStatus.OutOfStock ||
        !props.product?.is_salable
      );
    });

    const isSoldOut = computed<boolean>(() => {
      return (
        (props.product?.stock_status === ProductStockStatus.OutOfStock ||
          !props.product?.is_salable) &&
        !getProductReplenishable(props.product)
      );
    });

    const canNotify = computed<boolean>(() => {
      return (
        (props.product?.stock_status === ProductStockStatus.OutOfStock ||
          !props.product?.is_salable) &&
        getProductReplenishable(props.product)
      );
    });

    const productShortDescription = computed(
      () => props.product?.pdp_short_description || ''
    );

    const productLongDescription = computed(
      () => props.product?.pdp_long_description || ''
    );

    const shouldRenderDescription = computed(() =>
      Boolean(
        productShortDescription.value?.trim() ||
          productLongDescription.value?.trim()
      )
    );

    const addProductToCart = (product) => {
      if (isMerging.value) {
        return;
      }

      addItemToCart(product);
    };

    const route = useRoute();
    const isEngravingMode = computed<boolean>(
      () => route.value?.query?.engraving === '1'
    );

    const goToEngravingInfo = () => {
      openEngravingInfo();
    };

    const handleResize = debounce((entries: ResizeObserverEntry[]) => {
      const entry = entries[0];
      if (entry && entry.target === relatedProductsWrapp.value) {
        relatedProductsWidth.value = entry.contentRect.width;
      }
    }, RESIZE_DEBOUNCE_DELAY);

    const disconnectResizeObserver = () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
        resizeObserver = null;
      }
      relatedProductsWidth.value = 0;
    };

    watch(
      () => isEngravingInfoOpen.value,
      (newValue: boolean) => {
        productStore.isEngravingInfoOpen = newValue;
      }
    );

    watch(
      relatedProductsWrapp,
      (element) => {
        if (element) {
          relatedProductsWidth.value = element.clientWidth;
          disconnectResizeObserver();
          resizeObserver = new ResizeObserver(handleResize);
          resizeObserver.observe(element);
        } else {
          disconnectResizeObserver();
        }
      },
      { immediate: true }
    );

    const getRelatedProducts = computed(() => {
      if (!props.product?.revamp_related_products?.length) return [];
      return [...props.product?.revamp_related_products].sort(
        (item1, item2) => +item1.position - +item2.position
      );
    });
    const getProductSKU = computed(() => props.product?.sku);
    const productTitleData = computed<ProductTitleData | null>(() => {
      const {
        identity_name: productTitle,
        detailed_description: productDescription
      } = props.product || {};

      if (productTitle && productDescription) {
        return {
          title: productTitle,
          description: productDescription
        };
      }

      return null;
    });

    const getRegularPrice = (product) => {
      return validateFraction(productGetters.getPrice(product).regular);
    };

    const getInformationPleatsPanelData = computed(() => {
      const availablePleats = [
        'description',
        'deco_know_how',
        'usage_tips',
        'formule',
        'deco_material_type',
        'ingredients'
      ];
      return availablePleats.reduce((memo, attributeCode) => {
        if (props.product[attributeCode]) {
          memo.push({
            key:
              attributeCode === 'description'
                ? props.product[attributeCode].label
                : props.product[attributeCode][0]?.label,
            value:
              attributeCode === 'description'
                ? props.product[attributeCode]['html']
                : props.product[attributeCode][0]['value']
          });
        }
        return memo;
      }, []);
    });

    const crossSellProducts = computed<ProductInterface[]>(() => {
      return (
        props.product?.crosssell_products?.filter(
          (product) => product.is_salable
        ) ?? []
      );
    });

    const isSoclozEnabled = computed<boolean>(() => {
      const isReservationEnabled =
        config.value?.vaimo_socloz_reservation_enable === '1';

      return isReservationEnabled && !productData.value.isPreOrder;
    });

    const getAndTriggerSoClozScript = async () => {
      try {
        const result = (await $vsf.$magento.api.customQueryCacheable({
          query: soClozScript,
          queryVariables: {
            product_id: props.product?.id
          }
        })) as ApolloQueryResult<SoCloZQueryReturnType>;

        if (!result) {
          throw new Error('No result returned from SoCloz script query.');
        }

        // @ts-expect-error customQueryCacheable returns errorsList
        const { data, errorsList } = result;

        if (errorsList && errorsList.length > 0) {
          throw new Error(
            `GraphQL errors: ${errorsList.map((e) => e.message).join(', ')}`
          );
        }

        if (!data) {
          throw new Error('No data returned from SoCloz script query.');
        }

        soClozHtml.value = data?.reservationButton?.html;
        const buttonSpliterStart = data?.reservationButton?.html
          .toString()
          .split('<button ');
        const buttonSpliterEnd = buttonSpliterStart[1].split('</button>');
        soClozHtml.value = buttonSpliterEnd[1];

        const startIndex = soClozHtml.value.indexOf('(function ()');
        const soclozScriptString = soClozHtml.value
          .slice(startIndex)
          .replace(/<\/script>\s*<\/div>\s*$/, '');
        const soclozScript = new Function(soclozScriptString);
        soclozScript();
      } catch (err) {
        Logger.error('[SoCloz]', err);
      }
    };

    onBeforeMount(async () => {
      await getAndTriggerSoClozScript();
      await getEstimatedDeliveryDate();
      loadKlarnaScript();
      nextTick(() => {
        refreshKlarnaMessaging();
      });
    });

    const engravingStatus = computed(() => props.engravingConfig?.status);

    const unwatch = watch(
      [engravingStatus, isEngravingMode],
      ([newEngravingStatus, newIsEngravingMode]) => {
        if (newIsEngravingMode && newEngravingStatus === 'enabled') {
          nextTick(() => goToEngravingInfo());

          if (typeof unwatch === 'function') {
            unwatch();
          }
        }
      },
      { immediate: true }
    );

    const productAdditionalData = computed(() => {
      const getEditorialLabels = props.product?.editorial?.reduce(
        (memo, cur) => {
          if (cur?.label !== 'false') {
            memo.push(cur.label);
          }
          return memo;
        },
        []
      );

      return {
        editorial: getEditorialLabels,
        engraving: engravingStatus.value
      };
    });

    const hasEngraving = computed<boolean>(() => {
      return productAdditionalData.value?.engraving === 'enabled';
    });

    const footerElement = ref<HTMLElement | null>(null);
    const actionPanelArea = ref<HTMLElement | null>(null);
    const fixedPanel = ref<HTMLElement | null>(null);

    onMounted(() => {
      footerElement.value = document.querySelector<HTMLElement>(
        '.app-footer__wrapper'
      );
    });

    // Fixed Panel Intersection observer
    // - provide footerElement to hide the fixed panel when intersecting
    // - provide actionPanelArea to hide the fixed panel when intersecting
    // - offset to trigger the fixed panel a bit earlier
    const actionPanelElementOffset = 60;
    const { isPanelFixed } = useFixedPanel(
      footerElement,
      actionPanelArea,
      actionPanelElementOffset
    );

    const fixedPanelData = {
      footerElement,
      fixedPanel,
      actionPanelArea,
      isPanelFixed
    };

    const productCategories = product.value?.categories ?? [];
    /* OTIz = id 923 -> category only for candles */
    const isCandle = productCategories.find((item) => item.uid === 'OTIz')
      ? true
      : false;

    const getClickStorePickupDetails = () => {
      const categories = getBreadcrumbs(
        product.value,
        Array.isArray(productCategories) ? [...productCategories].pop() : null
      )
        .slice(0, -1)
        .map((cat) => cat.text);
      const regularPrice = productGetters.getPrice(product.value).regular;

      $gtm.push({
        event: 'clickStorePickup',
        item_name: product.value?.name,
        item_id: product.value?.sku,
        price: formatEventPrice(regularPrice),
        item_brand: itemBrand,
        item_category: categories[0] || '',
        item_category2: categories[1] || '',
        item_category3: categories[2] || '',
        item_variant: getEditorialData(product.value),
        item_list_name: 'pdp'
      });

      if (isSoclozEnabled.value) {
        observer.value = new MutationObserver((mutationsList) => {
          // WCAG 2.4.3 partial fix: move focus inside the iframe popup when it appears
          // and place focus back on previously focused element on popup close
          for (let mutation of mutationsList) {
            let soClozIframe = null;
            if (
              mutation.addedNodes.length &&
              (soClozIframe = getSoClozIframe(Array.from(mutation.addedNodes)))
            ) {
              soClozIframe.focus();
            } else if (
              mutation.removedNodes.length &&
              getSoClozIframe(Array.from(mutation.removedNodes))
            ) {
              soClozButton.value?.focus();
            }
          }
        });
        observer.value.observe(document, { childList: true, subtree: true });
      }
    };

    const cmsBlock = ref<CmsBlock>({});
    const cmsBlocks = ref<CmsBlock[]>([]);
    const isCmsSidebarVisible = ref<boolean>(false);
    const openCmsBlockByType = (identifier: string): void => {
      const currentBlock = cmsBlocks.value.find(
        (block) => block?.identifier === identifier
      );
      if (currentBlock) {
        cmsBlock.value = {
          content: currentBlock.content,
          title: currentBlock.title
        };
        isCmsSidebarVisible.value = true;
      }
    };

    const hideCmsSidebar = () => {
      isCmsSidebarVisible.value = false;
      cmsBlock.value = {};
    };

    const soClozButton = ref(null);
    const getSoClozIframe = (nodes) => {
      for (let node of nodes) {
        if (
          node instanceof HTMLElement &&
          node.nodeName === 'IFRAME' &&
          node.id === 'socloz-popup'
        ) {
          return node;
        }
      }
      return false;
    };

    const scrollToDescription = () => {
      scrollToElement('#description-section', {
        behavior: 'smooth',
        block: 'center',
        focusScrolledEl: true
      });
    };

    onMounted(async () => {
      cmsBlocks.value = await loadBlocks({
        identifiers: ['pdp_returns', 'pdp_delivery']
      });
    });

    onUnmounted(() => {
      if (isSoclozEnabled.value && observer.value) {
        observer.value.disconnect();
      }
    });

    const productData = computed(() => ({
      subtitle: getProductSubtitle(props.product),
      pdpDescription: getProductPDPCombinedDescription(props.product),
      fallbackName: getProductFallbackName(props.product),
      variantName: getProductVariantName(props.product),
      capacity: getProductCapacity(props.product),
      isReplenishable: getProductReplenishable(props.product),
      isPreOrder: isProductPreoderEnabled(props.product),
      preOrderAddToCartLabel: getProductPreorderButtonLabel(props.product),
      preOrderNoticeLabel: getProductPreorderStockNotice(props.product),
      showEstimationDeliveryDate: shouldShowEstimationDeliveryDate(
        props.product
      )
    }));

    const engravingPleatData = computed<{
      text: string;
      description: string;
    }>(() => ({
      text: props.engravingConfig?.pleat_text || '',
      description: props.engravingConfig?.pleat_description || ''
    }));

    const engravingData = {
      hasEngraving,
      isEngravingInfoOpen,
      engravingPleatData,
      goToEngravingInfo,
      openEngravingInfo,
      hideEngravingInfo
    };

    const notifyMeData = {
      notifyMe,
      openNotifyMeModal
    };

    return {
      ...fixedPanelData,
      ...engravingData,
      ...notifyMeData,
      productData,
      shouldShowEstimatedDelivery,
      productTitleData,
      productAdditionalData,
      crossSellProducts,
      getInformationPleatsPanelData,
      getRegularPrice,
      getBasePrice,
      isDesktop,
      getProductSKU,
      addProductToCart,
      loading,
      soClozHtml,
      hideSidebar,
      isInformationPleatsSidebarOpen,
      handlePleatClick,
      pleatData,
      productPrice,
      productSpecialPrice,
      isOutOfStock,
      isSoldOut,
      canNotify,
      getRelatedProducts,
      formatedDate,
      getClickStorePickupDetails,
      convertToMinorUnits,
      transformedLocale,
      isKlarnaEnabled,
      isPriceEligibleForInstalments,
      productImages,
      openCmsBlockByType,
      isCmsSidebarVisible,
      cmsBlock,
      hideCmsSidebar,
      validateFraction,
      isCandle,
      isMerging,
      isSoclozEnabled,
      soClozButton,
      scrollToDescription,
      relatedProductsWidth,
      relatedProductsWrapp,
      productShortDescription,
      productLongDescription,
      shouldRenderDescription
    };
  }
});
